import ranges from "Common/dateTimeUtils/ranges";
import { OPTION_TYPE } from "Common/dictionaries/optionType";
import ScrolledDropdown, { TogglerWithFloatLabel } from "Components/scrolledDropdown";
import classNames from "classnames";
import get from "lodash/get";
import isUndefined from "lodash/isUndefined";
import React, { memo, useEffect, useMemo, useState } from "react";
import locale from "./optionForm.locale";
import { BoolWidget, DateTimeWidget, GeospatialWidget, ImageWidget, NumberWidget, TagsWidget, TextAreaWidget, TextWidget } from "./widgets";
import isNil from "lodash/isNil";
const SIZES = {
    MAX_TAGS_WIDGET_WIDTH: "40ch"
};
const keySelector = (e) => e.dict_id;
const textSelector = (e) => e.dict_value;
const requiredOptionValidationError = { message: locale.REQUIRED_OPTION_EMPTY_MESSAGE };
const requiredBoolOptionValidationError = { message: locale.REQUIRED_BOOL_OPTION_EMPTY_MESSAGE };
function OptionForm(props) {
    const [value, setValue] = useState(props.value);
    useEffect(() => {
        var _a;
        let timeout;
        if (value !== props.value) {
            if (props.debounce) {
                timeout = setTimeout(() => {
                    var _a;
                    (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, props.optionId, value);
                }, props.debounce);
            }
            else {
                (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, props.optionId, value);
            }
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [value]);
    const widget = useMemo(() => {
        const { optionId, optionType, optionName, optionDict, defaultValue } = props;
        if (optionType === OPTION_TYPE.DICTIONARY) {
            return (React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, className: classNames("dropdown-toggle-flex-space-between d-grid", props.widgetClassName), selectedKey: value, onSelectedChanged: setValue, items: optionDict, togglerComponent: React.createElement(TogglerWithFloatLabel, null), zIndex: props.zIndex, disabled: !props.onChange, maxWidth: "100%", validationErrorMessage: requiredOptionValidationError.message, validationStatus: props.isRequired && value === undefined ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.STRING) {
            return (React.createElement(TextWidget, { className: props.widgetClassName, value: isNil(value) ? value : String(value), onChange: setValue, label: optionName, labelMode: "floating", readOnly: !props.onChange, height: 44, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && !value ? "invalid" : "valid", maxLength: 99999999 }));
        }
        if (optionType === OPTION_TYPE.GEOSPATIAL) {
            return !props.onChange ? (React.createElement(TextAreaWidget, { defaultSingleLine: true, maxHeight: 72, className: props.widgetClassName, value: isNil(value) ? value : String(value), label: optionName, labelMode: "floating", readOnly: true, usePreviewButton: true, previewTitle: props.optionName, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && !value ? "invalid" : "valid" })) : (React.createElement(GeospatialWidget, { className: props.widgetClassName, value: isNil(value) ? value : String(value), onChange: setValue, height: 44, label: optionName, labelMode: "floating", zIndex: props.zIndex, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && !value ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.BOOL) {
            return (React.createElement(BoolWidget, { className: props.widgetClassName, value: value, onChange: setValue, disabled: !props.onChange, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredBoolOptionValidationError, validationStatus: props.isRequired && isUndefined(value) ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.DATETIME) {
            return (React.createElement(DateTimeWidget, { className: props.widgetClassName, useUtc: true, value: value, onChange: setValue, dateTimeLimits: {
                    max: ranges.TODAY().endDateTime
                }, label: optionName, labelMode: "floating", zIndex: props.zIndex, readOnly: !props.onChange, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && !value ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.INT) {
            return (React.createElement(NumberWidget, { className: props.widgetClassName, value: value, onChange: setValue, label: optionName, labelMode: "floating", readOnly: !props.onChange, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && isUndefined(value) ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.DECIMAL) {
            return (React.createElement(NumberWidget, { min: undefined, format: "decimal", className: props.widgetClassName, value: value, label: optionName, labelMode: "floating", onChange: setValue, readOnly: !props.onChange, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && isUndefined(value) ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.TAGS) {
            return (React.createElement(TagsWidget, { className: props.widgetClassName, width: props.widgetClassName === "w-100" ? "100%" : SIZES.MAX_TAGS_WIDGET_WIDTH, maxDisplayedTags: 5, value: value, onChange: setValue, placeholder: optionName, readOnly: !props.onChange, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: requiredOptionValidationError, validationStatus: props.isRequired && isUndefined(value) ? "invalid" : "valid" }));
        }
        if (optionType === OPTION_TYPE.IMAGE) {
            return (React.createElement(ImageWidget, { key: optionId, className: props.widgetClassName, value: value, defaultValue: defaultValue, size: 50, onChange: setValue, zIndex: props.zIndex, readOnly: !props.onChange, previewTitle: props.optionName, onGetPreview: props.onGetPreview, invalidMessage: props.isRequired && !value ? locale.REQUIRED_OPTION_EMPTY_MESSAGE : "" }));
        }
        return React.createElement("div", { className: props.widgetClassName }, value);
    }, [
        props.optionId,
        props.optionName,
        props.optionType,
        props.optionDict,
        props.isRequired,
        value,
        props.defaultValue,
        props.onChange,
        props.zIndex,
        props.widgetClassName,
        props.onGetPreview
    ]);
    const inner = useMemo(() => {
        if (props.optionType === OPTION_TYPE.TAGS || props.optionType === OPTION_TYPE.DICTIONARY) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classNames("text-light-on-surface-variant w-fit-content", props.titleClassName, props.isRequired && "with-required-mark") },
                    `${props.optionName}${props.useColon ? ":" : ""}`,
                    props.labelMark),
                widget));
        }
        if (props.optionType === OPTION_TYPE.IMAGE) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classNames("text-light-on-surface-variant w-fit-content", props.titleClassName, props.isRequired && "with-required-mark") }, "\u0424\u043E\u0442\u043E"),
                widget));
        }
        if (props.optionType === OPTION_TYPE.BOOL) {
            return (React.createElement("div", { className: classNames(props.optionType === OPTION_TYPE.BOOL
                    ? "flex-box-centered-content h-space-between-content"
                    : "") },
                React.createElement("div", { className: classNames("text-light-on-surface-variant w-fit-content", props.titleClassName, props.isRequired && "with-required-mark") },
                    `${props.optionName}${props.useColon ? ":" : ""}`,
                    props.labelMark),
                widget));
        }
        return widget;
    }, [
        widget,
        props.titleClassName,
        props.optionName,
        props.isRequired,
        props.useColon,
        props.labelMark,
        props.optionType
    ]);
    return props.container ? (React.cloneElement(props.container, {
        children: inner,
        className: classNames(get(props.container, "props.className"), props.className)
    })) : (React.createElement("div", { className: classNames("grid-cols-auto", props.className) }, inner));
}
export default memo(OptionForm);
OptionForm.defaultProps = {
    className: "overflow-hidden",
    titleClassName: "nowrap text-overflow-ellipsis",
    widgetClassName: "nowrap text-overflow-ellipsis"
};
